import { useEffect, useState } from 'react';
import {
  ChevronRightIcon,
  ChevronLeftIcon,
  XCircleIcon,
} from '@heroicons/react/solid';
import { Layout, Loader, TimeslotsTable } from '../components';
import { format, getISOWeek, getYear, isBefore, isToday } from 'date-fns';
import { getSession } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/router';
import { axiosInstance } from '../helpers';
import clsx from 'clsx';
import Select, { components } from 'react-select';
import Image from 'next/image';

const { Option } = components;
const IconOption = (props) => {
  return (
    <Option {...props}>
      <div className="flex space-x-2">
        {props.data?.logoUrl && (
          <Image
            src={props.data?.logoUrl}
            width={30}
            height={30}
            alt={props.data?.alias}
            className="rounded-full"
          />
        )}
        <span>{props?.data?.alias}</span>
      </div>
    </Option>
  );
};

function CalendarPage({ companies }) {
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const weekFromRouter = router.query?.week as string;

  const today = new Date();
  const year = weekFromRouter
    ? Number(weekFromRouter.split('-W')[0])
    : getYear(today);
  const week = weekFromRouter
    ? Number(weekFromRouter.split('-W')[1])
    : getISOWeek(today);

  const [firstDayOfTheWeek, setFirstDayOfTheWeek] = useState('');
  const [lastDayOfTheWeek, setLastDayOfTheWeek] = useState('');
  const [triggerBookAction, setTriggerBookAction] = useState(false);
  const axiosService = axiosInstance();

  const [companyAlias, setCompanyAlias] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (router.query?.companyAlias && !company) {
      setCompany(
        companies.find(
          (company) => company.alias === router.query?.companyAlias
        )
      );
    }
  }, [router.query]);

  const [calendarPath, setCalendarPath] = useState(
    `${companyAlias}/${year}-W${week}`
  );

  async function fetchCalendar() {
    setLoading(true);
    try {
      const { data } = await axiosService.get(
        `calendars/customer/${calendarPath}/engine/public`
      );
      setCalendar(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (company?.alias) {
      fetchCalendar();
    }
  }, [calendarPath]);

  useEffect(() => {
    if (company?.alias) {
      setCompanyAlias(company?.alias);
      setCalendarPath(`${company?.alias}/${year}-W${week}`);
    }
  }, [company]);

  useEffect(() => {
    if (triggerBookAction) {
      fetchCalendar();
    }
  }, [triggerBookAction]);

  const [hash, setHash] = useState('');

  useEffect(() => {
    router.push({
      query: {
        ...router.query,
        ...(company?.alias ? { companyAlias: company?.alias } : {}),
        week: calendarPath.split('/')[1],
      },
      hash,
    });
  }, [calendarPath, hash]);

  function handleTodayBtn() {
    setCalendarPath(`${companyAlias}/${getYear(today)}-W${getISOWeek(today)}`);
    setHash(format(new Date(today), 'EEEE').toLowerCase());
  }

  useEffect(() => {
    if (calendar?.weeks?.currentWeek?.days) {
      const daysCopy = [...calendar?.weeks?.currentWeek?.days];
      const [lastDay, ...rest] = [...daysCopy.reverse()];
      const firstDayDate = new Date(
        calendar?.weeks?.currentWeek?.days[0]?.date
      );
      const lastDayDate = new Date(lastDay?.date);
      setFirstDayOfTheWeek(format(firstDayDate, 'EE dd LLL'));
      setLastDayOfTheWeek(format(lastDayDate, 'EE dd LLL'));
    } else {
      setFirstDayOfTheWeek('');
      setLastDayOfTheWeek('');
    }
  }, [calendar?.weeks?.currentWeek?.days]);

  async function handleChangeCompany(option) {
    setCompany(option);
  }

  return (
    <Layout title="Calendar | slotit.io - book your slots" company={company}>
      {loading && <Loader />}
      <div className="max-w-8xl pb-12">
        <div className="pb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Book your slot
          </h3>
          <div className="sm:hidden">
            <div className="text-sm mr-2 font-medium text-gray-500">
              {firstDayOfTheWeek} - {lastDayOfTheWeek}
            </div>
            <h3 className="text-xl leading-6 font-medium text-gray-900">
              {calendar?.weeks?.currentWeek?.name}
            </h3>
          </div>
          <p className="hidden sm:block mt-1 max-w-2xl text-sm text-gray-500">
            Calendar details and application.
          </p>
        </div>

        {!loading && calendar?.weeks && !calendar?.weeks?.currentWeek?.enabled && (
          <div className="mb-8">
            <div className="bg-red-50 rounded-md p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon
                    className="h-5 w-5 text-red-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3 flex flex-col space-y-4 md:flex-row md:space-y-0 items-center">
                  <p className="text-sm text-red-700 mr-4">
                    There are no available slots for this week. Please select
                    another one.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="hidden sm:flex flex-col xl:flex-row space-y-4 xl:space-y-0 xl:space-x-4 mb-6">
          <div className="flex lg:w-48 space-x-4">
            <div>
              <div className="text-sm mr-2 font-medium text-gray-500">
                {firstDayOfTheWeek} - {lastDayOfTheWeek}
              </div>
              <h3 className="text-xl leading-6 font-medium text-gray-900">
                {calendar?.weeks?.currentWeek?.name}
              </h3>
            </div>
          </div>
          <div className="space-x-4 hidden sm:flex">
            <button
              onClick={() => handleTodayBtn()}
              type="button"
              className="w-1/3 lg:w-36 h-12 px-3 border border-transparent shadow-sm text-base leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Today
            </button>
          </div>

          <div className="items-center space-x-4 xl:w-96 hidden sm:flex">
            {calendar?.weeks?.previousWeek?.name ? (
              <button
                onClick={() => {
                  setCalendarPath(
                    `${companyAlias}/${calendar?.weeks?.previousWeek?.name}`
                  );
                  setHash('');
                }}
                type="button"
                className="w-1/2 inline-flex justify-center items-center px-3 h-12 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ChevronLeftIcon
                  className="-ml-0.5 mr-2 h-4 w-4"
                  aria-hidden="true"
                />
                Previous Week
              </button>
            ) : (
              <button
                type="button"
                className="w-1/2 inline-flex justify-center items-center px-3 h-12 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ChevronLeftIcon
                  className="-ml-0.5 mr-2 h-4 w-4"
                  aria-hidden="true"
                />
                Previous Week
              </button>
            )}
            {calendar?.weeks?.nextWeek?.name && (
              <button
                onClick={() => {
                  setCalendarPath(
                    `${companyAlias}/${calendar?.weeks?.nextWeek?.name}`
                  );
                  setHash('');
                }}
                type="button"
                className="w-1/2 inline-flex justify-center items-center px-3 h-12 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Next Week
                <ChevronRightIcon
                  className="ml-2 -mr-0.5 h-4 w-4"
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="w-full max-w-lg">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
              Select the company
            </h3>
            <Select
              id="search-field"
              options={companies}
              getOptionValue={(option) => `${(option as any).id}`}
              getOptionLabel={(option: any) => option.alias}
              components={{ Option: IconOption }}
              value={companies.find((c) => c.alias === company?.alias)}
              onChange={(option) => handleChangeCompany(option)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10">
              {company?.logoUrl && (
                <Image
                  src={company?.logoUrl}
                  width={40}
                  height={40}
                  alt={company?.name}
                  className="rounded-full"
                />
              )}
            </div>
            <h3 className="text-xl text-gray-500">{company?.alias}</h3>
          </div>
        </div>

        {!loading &&
        company?.alias &&
        (!calendar?.published || !calendar?.weeks?.currentWeek?.enabled) ? (
          <div className="flex justify-center mt-24">
            <p className="text-gray-400 text-2xl">
              There are no available slots for this week
            </p>
          </div>
        ) : (
          calendar && (
            <div
              key={calendar?.weeks?.currentWeek?.id}
              className="mx-auto relative"
            >
              <div className="space-y-6 xl:space-y-0 xl:grid xl:grid-cols-2 gap-8">
                {calendar?.weeks?.currentWeek?.days?.map((day) => {
                  return (
                    <div
                      id={format(new Date(day?.date), 'EEEE').toLowerCase()}
                      key={day?.id}
                      className={clsx({
                        'border-2 rounded-lg bg-gray-50': true,
                        'opacity-50': isBefore(
                          new Date(day.date),
                          new Date(today)
                        ),
                        'border-indigo-500 opacity-100': isToday(
                          new Date(day.date)
                        ),
                        'border-gray-200': !isToday(new Date(day.date)),
                      })}
                    >
                      <div className="px-4 py-6 flex justify-between border-b">
                        <div>
                          <div className="hidden sm:block text-sm mr-2 font-medium text-gray-500">
                            Date
                          </div>
                          <h3 className="text-lg sm:text-2xl leading-6 font-medium text-gray-900">
                            {format(new Date(day?.date), 'EEEE dd LLLL yyyy')}
                          </h3>
                        </div>
                      </div>
                      <TimeslotsTable
                        timeslots={day?.timeslots}
                        setTriggerBookAction={setTriggerBookAction}
                        axiosService={axiosService}
                        companyId={company?.id}
                        setLoading={setLoading}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )
        )}
      </div>
      <div className="flex items-center fixed bottom-0 left-0 right-0 z-10 sm:hidden space-x-0.5 bg-gray-200 border-t-2">
        <button
          onClick={() => handleTodayBtn()}
          type="button"
          className="w:2/8 sm:hidden h-12 px-3 border border-transparent shadow-sm text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-100"
        >
          Today
        </button>

        {calendar?.weeks?.previousWeek?.name ? (
          <button
            onClick={() => {
              setCalendarPath(
                `${companyAlias}/${calendar?.weeks?.previousWeek?.name}`
              );
              setHash('');
            }}
            type="button"
            className="w-full inline-flex justify-center items-center px-3 h-12 border border-transparent shadow-sm text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-100"
          >
            <ChevronLeftIcon
              className="-ml-0.5 mr-2 h-4 w-4"
              aria-hidden="true"
            />
            Previous
          </button>
        ) : (
          <button
            type="button"
            className="w-full inline-flex justify-center items-center px-3 h-12 border border-gray-300 shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-100 opacity-50"
          >
            <ChevronLeftIcon
              className="-ml-0.5 mr-2 h-4 w-4"
              aria-hidden="true"
            />
            Previous
          </button>
        )}
        {calendar?.weeks?.nextWeek?.name && (
          <button
            onClick={() => {
              setCalendarPath(
                `${companyAlias}/${calendar?.weeks?.nextWeek?.name}`
              );
              setHash('');
            }}
            type="button"
            className="w-full inline-flex justify-center items-center px-3 h-12 border border-transparent shadow-sm text-sm leading-4 font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-100"
          >
            Next
            <ChevronRightIcon
              className="ml-2 -mr-0.5 h-4 w-4"
              aria-hidden="true"
            />
          </button>
        )}
      </div>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  let companies = [];
  const session = getSession(context.req, context.res);

  if (session?.user) {
    return {
      redirect: {
        destination: '/dashboard/calendar',
        permanent: false,
      },
    }
  }

  const axiosService = axiosInstance();

  try {
    const { data } = await axiosService.get(`companies/engine/public`);
    companies = data;
  } catch (error) {
    console.log(error);
  }

  return {
    props: {
      companies,
    },
  };
}

export default CalendarPage;
